import {
    DropdownContent,
    DropdownMenu,
    DropdownMenuItem,
    DropdownMenuSeparator,
    DropdownTrigger,
    IconButton,
    Kebab,
    showToast,
} from "@bcc-ui/ui-components";
import classNames from "classnames";
import React from "react";
import { useTranslation } from "react-i18next";

import { LoadingIndicator } from "components/loading-indicator/LoadingIndicator";
import Modal from "components/modal/Modal";
import { AUTH_REPORT_TEMPLATE_DEFAULT, AUTH_REPORT_TEMPLATE_DELETE, AUTH_REPORT_TEMPLATE_EDIT } from "domain/authority";
import { reportTemplateService } from "services/report/erasure/ReportTemplateService";
import { Action, Category, usageStatisticsService } from "services/statistics/UsageStatisticsService";
import { userSessionService } from "services/user/UserSessionService";
import buttons from "styles/buttons.scss";
import form from "styles/form.scss";
import { resolveColor } from "utils/commonFunctions";

import testIds from "testIds.json";

interface Result {
    title: string;
    message: string;
    resultVisible: boolean;
}

interface Props {
    uuid: string;
    name: string;
    onTemplateDelete: () => void;
    onTemplateEdit: (uuid: string) => void;
    onTemplateSetDefault: () => void;
    tableRef: React.RefObject<HTMLElement>;
}

export const ReportTemplateTableKebabMenu = (props: Props): JSX.Element => {
    const { t } = useTranslation();
    const [deleteTemplateModalVisible, setDeleteTemplateModalVisible] = React.useState(false);
    const [result, setResult] = React.useState<Result>({
        title: "",
        message: "",
        resultVisible: false,
    });
    const [okClicked, setOkClicked] = React.useState(false);
    const { current: abortControllers } = React.useRef<AbortController[]>([]);
    const clipboardId = "uuid-" + props.uuid;

    const copyToClipboard = () => {
        navigator.clipboard.writeText(props.uuid);
        showToast(t("Common.copied"), "success");
    };

    const hideResultAndRedirectToRoot = () => {
        setResult((prevState) => ({ ...prevState, resultVisible: false }));
        props.onTemplateDelete();
    };

    const deleteTemplate = () => {
        const abortController = new AbortController();
        abortControllers.push(abortController);
        reportTemplateService
            .deleteTemplate(props.uuid, abortController)
            .then(() => {
                setDeleteTemplateModalVisible(false);
                setResult({
                    title: t("DeleteTemplate.templateDeleted.templateDeletedTitle"),
                    message: t("DeleteTemplate.templateDeleted.successMessage", { name: props.name }),
                    resultVisible: true,
                });
                setOkClicked(false);
            })
            .catch(() => {
                if (!abortController.signal.aborted) {
                    setOkClicked(false);
                    setDeleteTemplateModalVisible(false);
                    setResult({
                        title: t("DeleteTemplate.templateDeleted.templateNotDeletedTitle"),
                        message: t("DeleteTemplate.templateDeleted.failureMessage"),
                        resultVisible: true,
                    });
                }
            });
    };

    const setDefaultTemplate = (uuid: string) => {
        const abortController = new AbortController();
        abortControllers.push(abortController);
        reportTemplateService.setDefault(uuid, abortController).then(() => {
            props.onTemplateSetDefault();
        });
    };

    React.useEffect(() => {
        return () => {
            abortControllers.forEach((abortController) => abortController.abort());
        };
    }, []);

    const handleTemplateDelete = () => {
        setOkClicked(true);
        deleteTemplate();
    };

    const isEditable = props.uuid !== "default";

    return (
        <>
            <DropdownMenu
                testId={testIds.common.primaryView.table.kebabMenu.itself}
                position={"bottom-start"}
                dropdownOffset={"Xxs"}
                closeOnSelection
                flipOptions={{
                    fallbackPlacements: ["top-start"],
                }}
            >
                <DropdownTrigger>
                    <IconButton>
                        <Kebab color={resolveColor("--theme-icon-fill-default")} />
                    </IconButton>
                </DropdownTrigger>
                <DropdownContent containerRef={props.tableRef}>
                    <DropdownMenuItem
                        id={clipboardId}
                        onClick={copyToClipboard}
                        testId={testIds.workArea.report.erasure.reportTemplate.table.kebabMenu.copyButton}
                    >
                        {t("TemplatesTable.copyUuid")}
                    </DropdownMenuItem>

                    {isEditable && userSessionService.userHasAllAuthorities([AUTH_REPORT_TEMPLATE_EDIT]) && (
                        <DropdownMenuItem
                            onClick={() => {
                                props.onTemplateEdit(props.uuid);
                                usageStatisticsService.sendEvent({
                                    category: Category.REPORT_TEMPLATE,
                                    action: Action.EDIT_REPORT_TEMPLATE,
                                });
                            }}
                            testId={testIds.workArea.report.erasure.reportTemplate.table.kebabMenu.editButton}
                        >
                            {t("EditTemplate.tooltipEdit")}
                        </DropdownMenuItem>
                    )}

                    {userSessionService.userHasAllAuthorities([AUTH_REPORT_TEMPLATE_DEFAULT]) && (
                        <DropdownMenuItem
                            onClick={() => {
                                setDefaultTemplate(props.uuid);
                                usageStatisticsService.sendEvent({
                                    category: Category.REPORT_TEMPLATE,
                                    action: Action.SET_DEFAULT_REPORT_TEMPLATE,
                                });
                            }}
                            testId={testIds.workArea.report.erasure.reportTemplate.table.kebabMenu.defaultButton}
                        >
                            {t("SetDefaultTemplate.tooltipSetDefault")}
                        </DropdownMenuItem>
                    )}

                    {isEditable && userSessionService.userHasAllAuthorities([AUTH_REPORT_TEMPLATE_DELETE]) && (
                        <>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem
                                errorStyle
                                onClick={() => {
                                    setDeleteTemplateModalVisible(true);
                                    usageStatisticsService.sendEvent({
                                        category: Category.REPORT_TEMPLATE,
                                        action: Action.REMOVE_REPORT_TEMPLATE,
                                    });
                                }}
                                testId={testIds.workArea.report.erasure.reportTemplate.table.kebabMenu.deleteButton}
                            >
                                {t("DeleteTemplate.tooltipDelete")}
                            </DropdownMenuItem>
                        </>
                    )}
                </DropdownContent>
            </DropdownMenu>

            <Modal
                isOpen={deleteTemplateModalVisible}
                hideModal={() => setDeleteTemplateModalVisible(false)}
                modalTitle={t("DeleteTemplate.title")}
            >
                {okClicked ? (
                    <LoadingIndicator />
                ) : (
                    <>
                        {deleteTemplateModalVisible ? (
                            <>
                                <div>{t("DeleteTemplate.introductionMessage", { name: props.name })}</div>
                                <div className={classNames(form.buttonContainer)}>
                                    <button
                                        className={buttons.secondaryButtonWithoutIcon}
                                        onClick={() => setDeleteTemplateModalVisible(false)}
                                        data-testid={testIds.common.dialog.closeButton}
                                    >
                                        {t("Common.cancel")}
                                    </button>

                                    <button
                                        className={classNames(buttons.primaryButtonWithoutIcon)}
                                        data-testid={testIds.common.confirmationDialog.confirmButton}
                                        onClick={handleTemplateDelete}
                                    >
                                        {t("Common.delete")}
                                    </button>
                                </div>
                            </>
                        ) : (
                            ""
                        )}
                    </>
                )}
            </Modal>

            <Modal isOpen={result.resultVisible} hideModal={hideResultAndRedirectToRoot} modalTitle={result.title}>
                <div>{result.message}</div>
                <div>
                    <button
                        className={buttons.primaryOkButton}
                        onClick={hideResultAndRedirectToRoot}
                        data-testid={testIds.common.dialog.closeButton}
                    >
                        {t("Common.ok")}
                    </button>
                </div>
            </Modal>
        </>
    );
};
